import { useCallback, useEffect, useRef } from 'react';

import { useAuiState } from '@assistant-ui/react';
import { usePrevious } from 'react-use';

import { IS_PRODUCTION } from '@/common/constants';
import { useConfigValue } from '@/components/providers/config-provider';
import { useDictationSoundEffects } from '@/hooks/use-dictation-sound-effects';
import { useMicrophonePermission } from '@/hooks/use-microphone-permission';
import { useTrackEvent } from '@/hooks/use-track-event';
import { type DictationSoundVariant, useDictationSounds } from '@/lib/audio/dictation-sounds';
import { CHAT_ANALYTICS_EVENTS } from '@/lib/constants/post-message-types';
import { clearActiveDictationStop, setActiveDictationStop } from '@/lib/dictation/active-dictation';
import { DICTATION_ERROR_TYPE } from '@/lib/dictation/constants';
import { getDictationTelemetry } from '@/lib/dictation/dictation-telemetry';
import type { ComposerHandle, DictationProvider } from '@/lib/dictation/types';
import { stopActiveTts } from '@/lib/tts/active-tts';
import { TTS_STOP_REASONS } from '@/lib/tts/analytics-constants';
import type { SttConfig } from '@/lib/types/tenant';

interface UseDictationHandlersOptions {
  composerRuntime: ComposerHandle;
  threadId: string;
  dictationProvider: DictationProvider;
}

export function useDictationHandlers({
  composerRuntime,
  threadId,
  dictationProvider,
}: UseDictationHandlersOptions) {
  const dictationStatus = useAuiState(({ composer }) => composer.dictation?.status ?? null);
  const previousStatusType = usePrevious(dictationStatus?.type);
  const isDictating = dictationStatus != null;
  const {
    requestPermission,
    isRequesting: isRequestingMicPermission,
    isDenied: isMicPermissionDenied,
    wasJustDenied: wasMicJustDenied,
    clearJustDenied: clearMicJustDenied,
  } = useMicrophonePermission();
  const trackEvent = useTrackEvent();
  const dictationStartTimeRef = useRef<number | null>(null);

  // Sound effects live here, not in DictationButton: the button unmounts on end
  // before the 'running'→'ended' transition fires; the composer stays mounted.
  const sttConfig = useConfigValue<SttConfig>('meta.stt');
  const enableDictationSounds = sttConfig?.enableSounds ?? false;
  const dictationSoundVariant = sttConfig?.soundVariant as DictationSoundVariant | undefined;
  const { playStartSound, playConnectedSound, playStopSound } = useDictationSounds(
    enableDictationSounds,
    dictationSoundVariant ?? undefined,
  );
  useDictationSoundEffects({
    status: dictationStatus?.type,
    isRequestingMicPermission,
    playStartSound,
    playConnectedSound,
    playStopSound,
  });

  const startDictation = useCallback(async () => {
    if (isDictating || isRequestingMicPermission) return;

    const hasPermission = await requestPermission();
    if (!hasPermission) {
      trackEvent({
        event: CHAT_ANALYTICS_EVENTS.DICTATION_PERMISSION_DENIED,
        properties: { threadId, provider: dictationProvider },
      });
      return;
    }

    dictationStartTimeRef.current = Date.now();
    stopActiveTts(TTS_STOP_REASONS.DICTATION_INTERRUPTED);
    setActiveDictationStop();
    composerRuntime.startDictation();

    trackEvent({
      event: CHAT_ANALYTICS_EVENTS.DICTATION_STARTED,
      properties: { threadId, provider: dictationProvider },
    });
  }, [
    composerRuntime,
    requestPermission,
    isDictating,
    isRequestingMicPermission,
    trackEvent,
    threadId,
    dictationProvider,
  ]);

  const stopDictation = useCallback(() => {
    if (!IS_PRODUCTION) {
      console.info('[Composer] Stopping dictation: user clicked stop button');
    }
    composerRuntime.stopDictation();
  }, [composerRuntime]);

  // assistant-ui drops `composer.dictation` on end, so the end must be detected
  // as the active → (ended | gone) transition, not just → 'ended'.
  useEffect(() => {
    const wasActive = previousStatusType != null && previousStatusType !== 'ended';
    const hasEnded = dictationStatus == null || dictationStatus.type === 'ended';
    if (!wasActive || !hasEnded) return;

    const durationMs = dictationStartTimeRef.current
      ? Date.now() - dictationStartTimeRef.current
      : undefined;
    dictationStartTimeRef.current = null;
    clearActiveDictationStop();

    const { transcriptLength, errorType } = getDictationTelemetry();
    // `reason` only exists on the 'ended' snapshot; when status went straight to
    // null, fall back to error / 'transcribed' (text produced — the dashboard
    // upgrades this to 'submitted') / 'cancelled' (nothing captured).
    const outcome =
      dictationStatus?.type === 'ended'
        ? dictationStatus.reason
        : errorType
          ? 'error'
          : transcriptLength > 0
            ? 'transcribed'
            : 'cancelled';

    trackEvent({
      event: CHAT_ANALYTICS_EVENTS.DICTATION_ENDED,
      properties: {
        threadId,
        provider: dictationProvider,
        durationMs,
        outcome,
        transcriptLength,
      },
    });

    if (outcome === 'error') {
      trackEvent({
        event: CHAT_ANALYTICS_EVENTS.DICTATION_ERROR,
        properties: {
          threadId,
          provider: dictationProvider,
          errorType: errorType ?? DICTATION_ERROR_TYPE.UNKNOWN,
        },
      });
    }
  }, [dictationStatus, previousStatusType, trackEvent, threadId, dictationProvider]);

  return {
    isDictating,
    startDictation,
    stopDictation,
    isRequestingMicPermission,
    isMicPermissionDenied,
    wasMicJustDenied,
    clearMicJustDenied,
  };
}
